import type { SvgIconComponent } from '@mui/icons-material';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DownloadIcon from '@mui/icons-material/Download';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PaymentsOutlined from '@mui/icons-material/PaymentsOutlined';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CostingComponent } from 'screens/Authenticated/NewBooking/CostingComponent';
import axiosInstance from 'utils/axios';
import Currency from 'utils/hooks/getCurrency';
import { privileges } from 'utils/constants/privileges';
import { URLS } from 'utils/constants/urls';
import { createDropdownData } from 'utils/validators/HelperFunctions';
import { useSelector } from 'react-redux';
import { Privilege } from 'utils/redux/reducer/authentication-slice';
import Config from '../../../../utils/Config';
import strings from '../../../../common/Translation/Translate';
import { AddPaymentButton } from '../BookingDetailButtons/AddPayment';
import { AddReceivableButton } from '../BookingDetailButtons/AddReceivable';
import { getReceivableTypeLabel } from '../BookingDetailButtons/AddReceivable/receivableUtils';
import { ManageReceivableDrawer } from '../BookingDetailButtons/ManageReceivable';
import { SettleReceivableDrawer } from '../BookingDetailButtons/SettleReceivable';
import { BookingDetailSectionTitle } from './BookingDetailSectionTitle';

dayjs.extend(advancedFormat);

const paymentMethodChipSx = { backgroundColor: '#E8F0FE', color: '#1967D2' };
const RECEIVABLE_TYPE_CHIP_SX = { backgroundColor: '#E6F4EA', color: '#137333', fontWeight: 600, fontSize: 10 };

type SettlementEntryStatus = 'OPEN' | 'PARTIALLY_SETTLED' | 'SETTLED' | 'CANCELLED';

const formatLedgerDate = (value: unknown) => {
  if (!value) return '—';
  return dayjs(value as string).format('DD MMM YYYY');
};

const ReceivableStatusIndicator = ({ status }: { status: SettlementEntryStatus }) => {
  switch (status) {
    case 'SETTLED':
      return (
        <Chip
          label={strings.receivable_status_settled}
          size="small"
          icon={<CheckCircleOutlineIcon sx={{ fontSize: '14px !important' }} />}
          sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: '#E6F4EA', color: '#137333' }}
        />
      );
    case 'PARTIALLY_SETTLED':
      return (
        <Chip
          label={strings.receivable_status_partiallySettled}
          size="small"
          icon={<InfoOutlinedIcon sx={{ fontSize: '14px !important' }} />}
          sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: '#FFF4E5', color: '#D17A00' }}
        />
      );
    case 'CANCELLED':
      return (
        <Chip
          label={strings.receivable_status_cancelled}
          size="small"
          sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: '#F1F3F4', color: '#666' }}
        />
      );
    default:
      return (
        <Chip
          label={strings.receivable_status_open}
          size="small"
          icon={<WarningAmberOutlinedIcon sx={{ fontSize: '14px !important' }} />}
          sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: '#FCE8E6', color: '#C5221F' }}
        />
      );
  }
};

const compactTableSx = {
  '& .MuiTableCell-head': {
    fontSize: '0.7rem',
    lineHeight: 1.25,
    letterSpacing: 0.02,
    py: 0.75,
    px: 1,
  },
  '& .MuiTableCell-body': {
    fontSize: '0.75rem',
    lineHeight: 1.35,
    py: 0.5,
    px: 1,
  },
} as const;

function pickLineTaxBreakup(item: any): any[] | undefined {
  const raw = item?.taxBreakup ?? item?.tax_breakup;
  return Array.isArray(raw) ? raw : undefined;
}

/** API may send BigDecimal as number or string; CostingComponent expects finite numbers for breakdown rows. */
function toCostingNumber(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function normalizeCostingLineItems(items: any[] | undefined): any[] {
  if (!items?.length) return [];
  return items.map(item => {
    const tb = pickLineTaxBreakup(item);
    return {
      header: !!item.header,
      text: item.text ?? '',
      amount: item.amount ?? '—',
      unformattedAmount: toCostingNumber(item.unformattedAmount ?? item.unformatted_amount),
      grossAmount: toCostingNumber(item.grossAmount ?? item.gross_amount),
      formattedGrossAmount: item.formattedGrossAmount ?? item.formatted_gross_amount,
      itemDiscount: toCostingNumber(item.itemDiscount ?? item.item_discount),
      formattedItemDiscount: item.formattedItemDiscount ?? item.formatted_item_discount,
      serviceCharge: toCostingNumber(item.serviceCharge ?? item.service_charge),
      formattedServiceCharge: item.formattedServiceCharge ?? item.formatted_service_charge,
      allocatedOverallDiscount: toCostingNumber(
        item.allocatedOverallDiscount ?? item.allocated_overall_discount,
      ),
      formattedAllocatedOverallDiscount:
        item.formattedAllocatedOverallDiscount ?? item.formatted_allocated_overall_discount,
      allocatedDiscounts: (item.allocatedDiscounts ?? item.allocated_discounts)?.map((slice: any) => ({
        description: slice.description,
        amount: toCostingNumber(slice.amount),
        formattedAmount: slice.formattedAmount ?? slice.formatted_amount,
      })),
      taxableAmount: toCostingNumber(item.taxableAmount ?? item.taxable_amount),
      formattedTaxableAmount: item.formattedTaxableAmount ?? item.formatted_taxable_amount,
      displayAmount: toCostingNumber(item.displayAmount ?? item.display_amount),
      itemTotal: toCostingNumber(item.itemTotal ?? item.item_total),
      formattedItemTotal: item.formattedItemTotal ?? item.formatted_item_total,
      venueName: item.venueName ?? item.venue_name ?? undefined,
      dateLabel: item.dateLabel ?? item.date_label ?? undefined,
      eventSubtype: item.eventSubtype ?? item.event_subtype ?? undefined,
      timeLabel: item.timeLabel ?? item.time_label ?? undefined,
      sessionLabel: item.sessionLabel ?? item.session_label ?? undefined,
      status: item.status ?? undefined,
      statusLabel: item.statusLabel ?? item.status_label ?? undefined,
      eventSortKey: toCostingNumber(item.eventSortKey ?? item.event_sort_key),
      taxBreakup: tb?.length ? normalizeCostingLineItems(tb) : undefined,
    };
  });
}

export type LineItemPaymentSummary = {
  total: number | null | undefined;
  paid: number | null | undefined;
  due: number | null | undefined;
};

export interface LineItemBillingPaymentState {
  billingSummarySectionTitle: string;
  costingLoading: boolean;
  costingError: string | null;
  costingPage: { lineItems: any[]; taxTotals?: any[]; total: string } | null;
  billingPerGuestInsight: { label: string; formattedAmount: string } | undefined;
  paymentsLoading: boolean;
  lineItemPayments: any[];
  settlementEntries: any[];
  bookingAmountDue: number;
  paymentSummary: LineItemPaymentSummary | null;
  paymentMethods: any[];
  paymentMethodsLoading: boolean;
}

/** Single hook: one fetch each for billing summary and payments (use once per line item). */
export function useLineItemBillingPayment(bookingLineItem: any, paymentRefreshKey = 0): LineItemBillingPaymentState {
  const [lineItemPayments, setLineItemPayments] = useState<any[]>([]);
  const [settlementEntries, setSettlementEntries] = useState<any[]>([]);
  const [bookingAmountDue, setBookingAmountDue] = useState(0);
  const [paymentSummary, setPaymentSummary] = useState<LineItemPaymentSummary | null>(null);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false);

  const billingSummarySectionTitle =
    bookingLineItem?.hasActualization
      ? strings.actualizationSummary
      : strings.bookingDetails_section_billingBreakdown;

  useEffect(() => {
    const lineItemId = bookingLineItem?.id;
    if (lineItemId == null) {
      setLineItemPayments([]);
      setSettlementEntries([]);
      setBookingAmountDue(0);
      setPaymentSummary(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setPaymentsLoading(true);
      try {
        const { data } = await axiosInstance.get(URLS.getLineItemPaymentHistory(lineItemId));
        if (cancelled) return;
        setLineItemPayments(data?.payments || []);
        setSettlementEntries(data?.settlementEntries || []);
        setBookingAmountDue(Number(data?.bookingAmountDue) || 0);
        setPaymentSummary({
          total: data?.totalAmount,
          paid: data?.amountPaid,
          due: data?.amountDue,
        });
      } catch {
        if (!cancelled) {
          setLineItemPayments([]);
          setSettlementEntries([]);
          setBookingAmountDue(0);
          setPaymentSummary(null);
        }
      } finally {
        if (!cancelled) setPaymentsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bookingLineItem?.id, paymentRefreshKey]);

  useEffect(() => {
    const lineItemId = bookingLineItem?.id;
    if (lineItemId == null) {
      setPaymentMethods([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setPaymentMethodsLoading(true);
      try {
        const { status, data } = await axiosInstance.get(URLS.getPaymentMethods(lineItemId));
        if (cancelled) return;
        if (status === 200 && data) {
          setPaymentMethods(createDropdownData(data, ['id', 'name']));
        } else {
          setPaymentMethods([]);
        }
      } catch {
        if (!cancelled) setPaymentMethods([]);
      } finally {
        if (!cancelled) setPaymentMethodsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bookingLineItem?.id, paymentRefreshKey]);

  const [costingPage, setCostingPage] = useState<{
    lineItems: any[];
    taxTotals?: any[];
    total: string;
  } | null>(null);
  const [costingLoading, setCostingLoading] = useState(false);
  const [costingError, setCostingError] = useState<string | null>(null);

  const costingReloadDep = useMemo(
    () =>
      [
        bookingLineItem?.id,
        bookingLineItem?.guestCharges,
        bookingLineItem?.hallRental,
        bookingLineItem?.totalAddOnCost,
        bookingLineItem?.totalServiceCharge,
        bookingLineItem?.totalTax,
        bookingLineItem?.totalSurcharge,
        bookingLineItem?.totalCost,
        bookingLineItem?.actualTotalCost,
        bookingLineItem?.hasActualization,
      ].join('|'),
    [
      bookingLineItem?.id,
      bookingLineItem?.guestCharges,
      bookingLineItem?.hallRental,
      bookingLineItem?.totalAddOnCost,
      bookingLineItem?.totalServiceCharge,
      bookingLineItem?.totalTax,
      bookingLineItem?.totalSurcharge,
      bookingLineItem?.totalCost,
      bookingLineItem?.actualTotalCost,
      bookingLineItem?.hasActualization,
    ],
  );

  const billingPerGuestInsight = useMemo(() => {
    const guests = Number(bookingLineItem?.guaranteedGuests) || 0;
    if (guests <= 0) return undefined;
    const raw = bookingLineItem?.hasActualization
      ? (bookingLineItem?.actualTotalCost ?? bookingLineItem?.totalCost)
      : (bookingLineItem?.totalCost ?? bookingLineItem?.totalAmount ?? bookingLineItem?.total);
    const total = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(total) || total <= 0) return undefined;
    return {
      label: strings.bookingDetails_billing_perGuestEffectivePrice,
      formattedAmount: Currency(total / guests),
    };
  }, [
    bookingLineItem?.guaranteedGuests,
    bookingLineItem?.hasActualization,
    bookingLineItem?.actualTotalCost,
    bookingLineItem?.totalCost,
    bookingLineItem?.totalAmount,
    bookingLineItem?.total,
  ]);

  useEffect(() => {
    const id = bookingLineItem?.id;
    if (id == null) {
      setCostingPage(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setCostingLoading(true);
      setCostingError(null);
      try {
        const { data } = await axiosInstance.get(URLS.getBillingCostingSummary(id));
        if (cancelled) return;
        const totalDisplay =
          data?.totalUnformatted != null && Number.isFinite(Number(data.totalUnformatted))
            ? Currency(Number(data.totalUnformatted))
            : (data?.total ?? '—');
        setCostingPage({
          lineItems: normalizeCostingLineItems(data?.lineItems),
          taxTotals: normalizeCostingLineItems(data?.taxTotals),
          total: totalDisplay,
        });
      } catch (e: any) {
        if (!cancelled) {
          setCostingPage(null);
          setCostingError(
            e?.response?.data?.message ??
              e?.response?.data?.error_description ??
              strings.bookingDetails_billing_costingLoadError,
          );
        }
      } finally {
        if (!cancelled) setCostingLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [costingReloadDep]);

  return {
    billingSummarySectionTitle,
    costingLoading,
    costingError,
    costingPage,
    billingPerGuestInsight,
    paymentsLoading,
    lineItemPayments,
    settlementEntries,
    bookingAmountDue,
    paymentSummary,
    paymentMethods,
    paymentMethodsLoading,
  };
}

export function BillingBreakdownPaper({
  billingSummarySectionTitle,
  billingTitleIcon = ReceiptLongOutlined,
  costingLoading,
  costingError,
  costingPage,
  billingPerGuestInsight,
  downloadInvoice,
  downloadInvoiceLoading,
}: Pick<
  LineItemBillingPaymentState,
  'billingSummarySectionTitle' | 'costingLoading' | 'costingError' | 'costingPage' | 'billingPerGuestInsight'
> & {
  billingTitleIcon?: SvgIconComponent;
  downloadInvoice?: () => void | Promise<void>;
  downloadInvoiceLoading?: boolean;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
      <BookingDetailSectionTitle icon={billingTitleIcon}>{billingSummarySectionTitle}</BookingDetailSectionTitle>
      <Divider sx={{ mb: 1.25 }} />
      {costingLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
          <CircularProgress size={22} />
          <Typography variant="body2" color="text.secondary">
            {strings.bookingDetails_billing_loading}
          </Typography>
        </Box>
      )}
      {costingError && !costingLoading && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {costingError}
        </Alert>
      )}
      {costingPage && !costingLoading && (
        <Box
          sx={{
            px: { xs: 0, sm: 0.5 },
            py: 0,
            '& .MuiCard-root': { width: '100%' },
          }}>
          <CostingComponent
            pageResponse={costingPage}
            perGuestInsight={billingPerGuestInsight}
            grandTotalLabel={strings.bookingDetails_billing_totalAmountLine}
            grandTotalEmphasized
            downloadAction={
              downloadInvoice
                ? {
                    onClick: downloadInvoice,
                    loading: !!downloadInvoiceLoading,
                    label: 'Download Invoice',
                  }
                : undefined
            }
          />
        </Box>
      )}
    </Paper>
  );
}

type PaymentHistoryPaperProps = Pick<
  LineItemBillingPaymentState,
  | 'paymentsLoading'
  | 'lineItemPayments'
  | 'settlementEntries'
  | 'bookingAmountDue'
  | 'paymentMethods'
  | 'paymentMethodsLoading'
> & {
  bookingLineItem: any;
  onSuccess: () => void;
};

export function PaymentHistoryPaper({
  paymentsLoading,
  lineItemPayments,
  settlementEntries,
  bookingAmountDue,
  paymentMethods,
  paymentMethodsLoading,
  bookingLineItem,
  onSuccess,
}: PaymentHistoryPaperProps) {
  const IsPrivilege = useSelector(Privilege);
  const booking = bookingLineItem?.booking;
  const bookingId = booking?.id;

  const [settleTarget, setSettleTarget] = useState<any>(null);
  const [manageTarget, setManageTarget] = useState<any>(null);
  const [cancelTarget, setCancelTarget] = useState<any>(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState('');

  const isConfirmed = String(bookingLineItem?.status || '').toUpperCase() === 'CONFIRMED';
  const canAcceptPayment = IsPrivilege.includes(privileges.acceptPayment);
  const canManageLedgers = !bookingLineItem?.deleted && IsPrivilege.includes(privileges.manageLedgers);

  const showAddPayment =
    bookingLineItem?.id &&
    !bookingLineItem.deleted &&
    isConfirmed &&
    canAcceptPayment &&
    !paymentMethodsLoading &&
    paymentMethods.length > 0;

  const canAddReceivable = bookingLineItem?.id && !bookingLineItem.deleted && isConfirmed && canAcceptPayment;

  const settlementPaymentMethods = useMemo(
    () =>
      paymentMethods.filter(
        (method: any) =>
          !['BILL_TO_COMPANY', 'BTC', 'CITY_LEDGER', 'CL'].includes(String(method.value || '').toUpperCase()),
      ),
    [paymentMethods],
  );

  const maxReceivableAmount = useMemo(() => {
    const pendingReceivables = (settlementEntries || []).reduce((sum: number, entry: any) => {
      const status = String(entry?.status || '').toUpperCase();
      if (status === 'SETTLED' || status === 'CANCELLED') {
        return sum;
      }
      return sum + (Number(entry?.pendingAmount) || 0);
    }, 0);
    return Math.max(0, bookingAmountDue - pendingReceivables);
  }, [bookingAmountDue, settlementEntries]);

  const hasActiveReceipts = (entry: any) => (entry?.receipts || []).some((receipt: any) => !receipt.reversed);

  const canSettleReceivable = (entry: any) => {
    const pendingNum = Number(entry?.pendingAmount) || 0;
    const entryStatus = String(entry?.status || '').toUpperCase();
    return canManageLedgers && pendingNum > 0 && entryStatus !== 'SETTLED' && entryStatus !== 'CANCELLED';
  };

  const handleCancelReceivable = useCallback(async () => {
    if (!cancelTarget || !bookingId) return;
    setCancelLoading(true);
    setCancelError('');
    try {
      await axiosInstance.post(URLS.cancelSettlementEntry(bookingId, cancelTarget.id));
      setCancelTarget(null);
      onSuccess();
    } catch (error: any) {
      setCancelError(
        error.response?.data?.error_description ||
          error.response?.data?.message ||
          strings.something_went_wrong,
      );
    } finally {
      setCancelLoading(false);
    }
  }, [cancelTarget, bookingId, onSuccess]);

  const sectionActions =
    showAddPayment || canAddReceivable ? (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, flexShrink: 0, '& .MuiButton-root': { my: 0 } }}>
        {showAddPayment && (
          <AddPaymentButton
            cashLimit={bookingLineItem.docAboveCashLimit ?? 50000}
            paymentMethods={paymentMethods}
            onSuccess={onSuccess}
            url={URLS.addPaymentForLineItem(bookingLineItem.id)}
            size="small"
            variant="contained"
            color="success"
          />
        )}
        {canAddReceivable && bookingId && (
          <AddReceivableButton
            bookingId={bookingId}
            onSuccess={onSuccess}
            maxReceivableAmount={maxReceivableAmount}
            defaultCompanyName={booking?.companyName}
            defaultGstNumber={booking?.gst}
          />
        )}
      </Box>
    ) : undefined;

  return (
    <>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
        <BookingDetailSectionTitle icon={PaymentsOutlined} action={sectionActions} actionBelow>
          {strings.bookingDetails_section_paymentHistory}
        </BookingDetailSectionTitle>
        <Divider sx={{ mb: 1 }} />
        {paymentsLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              {strings.bookingDetails_paymentHistory_loading}
            </Typography>
          </Box>
        ) : (
          <>
            {lineItemPayments.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: settlementEntries.length > 0 ? 1.5 : 0 }}>
                {strings.bookingDetails_paymentHistory_empty}
              </Typography>
            ) : (
              <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto', mb: settlementEntries.length > 0 ? 1.5 : 0 }}>
                <Table size="small" sx={compactTableSx}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>{strings.paymentDetails_text_date}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{strings.paymentDetails_title_method}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{strings.paymentDetails_text_reference}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {strings.paymentDetails_title_paid}
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, width: 56 }}>
                        {strings.paymentDetails_text_attachment}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lineItemPayments.map((payment: any) => (
                      <TableRow
                        key={payment.id ?? `${payment.paymentDate}-${payment.paymentReference}`}
                        sx={{
                          '&:nth-of-type(odd)': { backgroundColor: 'rgba(0, 0, 0, 0.02)' },
                          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                        }}>
                        <TableCell>
                          <Typography variant="caption" sx={{ fontSize: '0.75rem', display: 'block' }}>
                            {payment.paymentDate ? dayjs(payment.paymentDate).format('DD MMM YYYY') : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={payment.paymentMethodName || '—'}
                            size="small"
                            sx={{ fontWeight: 600, fontSize: 10, height: 20, ...paymentMethodChipSx }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.75rem', display: 'block' }}>
                            {payment.paymentReference || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: '0.75rem',
                              fontWeight: 700,
                              fontVariantNumeric: 'tabular-nums',
                              display: 'block',
                            }}>
                            {payment.formattedAmountPaid || Currency(payment.amountPaid) || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {payment.attachmentUrl ? (
                            <Tooltip title={strings.paymentDetails_text_attachment}>
                              <IconButton
                                component={Link}
                                href={Config.BaseURL + payment.attachmentUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                size="small"
                                color="primary"
                                sx={{ p: 0.5 }}>
                                <DownloadIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>
                              —
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Divider sx={{ my: 1.25 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8125rem', mb: 0.75 }}>
              {strings.receivable_section_title}
            </Typography>

            {settlementEntries.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {strings.receivable_empty_subtitle}
              </Typography>
            ) : (
              <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                <Table size="small" sx={compactTableSx}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>{strings.receivable_column_date}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{strings.receivable_column_party}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {strings.receivable_column_amount}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {strings.receivable_column_received}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {strings.receivable_column_pending}
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>
                        {strings.receivable_column_status}
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, width: 88 }}>
                        {strings.paymentDetails_column_actions}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {settlementEntries.map((entry: any) => {
                      const pendingNum = Number(entry.pendingAmount) || 0;
                      const pendingColor = pendingNum <= 0 ? 'text.secondary' : '#D93025';
                      return (
                        <TableRow
                          key={`settlement-${entry.id}`}
                          sx={{
                            '&:nth-of-type(odd)': { backgroundColor: 'rgba(0, 0, 0, 0.02)' },
                            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                          }}>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 600, display: 'block' }}>
                              {formatLedgerDate(entry.createdAt)}
                            </Typography>
                            <Chip
                              label={getReceivableTypeLabel(entry.type)}
                              size="small"
                              sx={{ ...RECEIVABLE_TYPE_CHIP_SX, mt: 0.5, height: 18, fontSize: 10 }}
                            />
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 500, display: 'block' }}>
                              {entry.companyName || '—'}
                            </Typography>
                            {entry.remarks && (
                              <Tooltip title={entry.remarks} arrow placement="top">
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    display: 'block',
                                    mt: 0.25,
                                    fontSize: '0.7rem',
                                    maxWidth: 120,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}>
                                  {entry.remarks}
                                </Typography>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                            <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                              {entry.formattedAmount || Currency(entry.amount)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                            <Typography
                              variant="caption"
                              sx={{ fontSize: '0.75rem', color: '#188038', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                              {entry.formattedReceivedAmount || Currency(entry.receivedAmount)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '0.75rem',
                                color: pendingColor,
                                fontWeight: pendingNum <= 0 ? 500 : 600,
                                fontVariantNumeric: 'tabular-nums',
                              }}>
                              {entry.formattedPendingAmount || Currency(entry.pendingAmount)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ verticalAlign: 'top' }}>
                            <ReceivableStatusIndicator status={entry.status as SettlementEntryStatus} />
                          </TableCell>
                          <TableCell align="center" sx={{ verticalAlign: 'top' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                              {entry.attachmentUrl && (
                                <Tooltip title={strings.paymentDetails_text_attachment} arrow>
                                  <IconButton
                                    size="small"
                                    component={Link}
                                    href={Config.BaseURL + entry.attachmentUrl}
                                    target="_blank"
                                    download
                                    sx={{ color: '#188038', p: 0.5 }}>
                                    <DownloadIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {canSettleReceivable(entry) && (
                                <Tooltip title={strings.receivable_button_settle} arrow>
                                  <IconButton size="small" onClick={() => setSettleTarget(entry)} sx={{ color: '#188038', p: 0.5 }}>
                                    <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {hasActiveReceipts(entry) && canManageLedgers && (
                                <Tooltip title={strings.receivable_button_manage} arrow>
                                  <IconButton
                                    size="small"
                                    onClick={() => setManageTarget(entry)}
                                    sx={{ color: 'warning.main', p: 0.5 }}>
                                    <ReplayIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {entry.canCancel && canAddReceivable && (
                                <Tooltip title={strings.receivable_button_cancel} arrow>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setCancelError('');
                                      setCancelTarget(entry);
                                    }}
                                    sx={{ color: 'error.main', p: 0.5 }}>
                                    <BlockOutlinedIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Paper>

      {bookingId && (
        <>
          <SettleReceivableDrawer
            open={Boolean(settleTarget)}
            onClose={() => setSettleTarget(null)}
            bookingId={bookingId}
            settlementEntry={settleTarget}
            paymentMethods={settlementPaymentMethods}
            onSuccess={onSuccess}
          />
          <ManageReceivableDrawer
            open={Boolean(manageTarget)}
            onClose={() => setManageTarget(null)}
            bookingId={bookingId}
            settlementEntry={manageTarget}
            onSuccess={onSuccess}
          />
        </>
      )}

      <Dialog
        open={Boolean(cancelTarget)}
        onClose={() => !cancelLoading && setCancelTarget(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
        {cancelTarget && (
          <>
            <Box sx={{ px: 3, py: 2.5, bgcolor: 'rgba(211, 47, 47, 0.06)', borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    bgcolor: 'error.main',
                    color: 'error.contrastText',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                  <WarningAmberOutlinedIcon />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3, mb: 0.5 }}>
                    {strings.receivable_cancel_title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {strings.receivable_cancel_prompt}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
              <Paper variant="outlined" sx={{ px: 1.5, py: 1, borderRadius: 1.5, bgcolor: 'rgba(0, 0, 0, 0.02)' }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                  <Chip label={getReceivableTypeLabel(cancelTarget.type)} size="small" sx={{ height: 22, ...RECEIVABLE_TYPE_CHIP_SX }} />
                  <Typography variant="body2" fontWeight={500}>
                    {cancelTarget.companyName || '—'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                    {cancelTarget.formattedAmount || Currency(cancelTarget.amount)}
                  </Typography>
                </Box>
              </Paper>
              {cancelError && (
                <Typography variant="body2" color="error" sx={{ mt: 1.5 }}>
                  {cancelError}
                </Typography>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
              <Button variant="outlined" onClick={() => setCancelTarget(null)} disabled={cancelLoading}>
                {strings.common_button_back}
              </Button>
              <Button
                color="error"
                variant="contained"
                onClick={handleCancelReceivable}
                disabled={cancelLoading}
                startIcon={cancelLoading ? <CircularProgress size={18} color="inherit" /> : <WarningAmberOutlinedIcon />}>
                {strings.receivable_cancel_confirm}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
}
